/**
 * Editor persistence: user levels + crash-safe autosave drafts.
 * IndexedDB in the browser (survives reloads, holds large levels); an
 * in-memory fallback keeps the module working in tests and degraded browsers
 * (with a console warning — never a silent downgrade).
 */
import type { LevelData } from '../format/types';
import { parseLevel, stringifyLevel } from '../format/serialize';

export interface StoredLevel {
  /** Storage id (independent of level name; names may collide). */
  id: string;
  name: string;
  updatedAt: string;
  /** Serialized level text (validated on read, not trusted blindly). */
  text: string;
}

const DB_NAME = 'shape-shifters-editor';
const DB_VERSION = 1;
const LEVELS = 'levels';
const DRAFTS = 'drafts';

interface Backend {
  put(store: string, value: StoredLevel): Promise<void>;
  get(store: string, id: string): Promise<StoredLevel | undefined>;
  delete(store: string, id: string): Promise<void>;
  list(store: string): Promise<StoredLevel[]>;
}

class IdbBackend implements Backend {
  private dbPromise: Promise<IDBDatabase>;

  constructor() {
    this.dbPromise = new Promise((resolve, reject) => {
      const req = indexedDB.open(DB_NAME, DB_VERSION);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(LEVELS)) db.createObjectStore(LEVELS, { keyPath: 'id' });
        if (!db.objectStoreNames.contains(DRAFTS)) db.createObjectStore(DRAFTS, { keyPath: 'id' });
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error ?? new Error('IndexedDB open failed'));
    });
  }

  private async tx<T>(
    store: string,
    mode: IDBTransactionMode,
    fn: (s: IDBObjectStore) => IDBRequest<T>,
  ): Promise<T> {
    const db = await this.dbPromise;
    return new Promise<T>((resolve, reject) => {
      const t = db.transaction(store, mode);
      const req = fn(t.objectStore(store));
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error ?? new Error('IndexedDB request failed'));
    });
  }

  put(store: string, value: StoredLevel): Promise<void> {
    return this.tx(store, 'readwrite', (s) => s.put(value)).then(() => undefined);
  }

  get(store: string, id: string): Promise<StoredLevel | undefined> {
    return this.tx<StoredLevel | undefined>(
      store,
      'readonly',
      (s) => s.get(id) as IDBRequest<StoredLevel | undefined>,
    );
  }

  delete(store: string, id: string): Promise<void> {
    return this.tx(store, 'readwrite', (s) => s.delete(id)).then(() => undefined);
  }

  list(store: string): Promise<StoredLevel[]> {
    return this.tx<StoredLevel[]>(
      store,
      'readonly',
      (s) => s.getAll() as IDBRequest<StoredLevel[]>,
    );
  }
}

class MemoryBackend implements Backend {
  private stores = new Map<string, Map<string, StoredLevel>>();

  private store(name: string): Map<string, StoredLevel> {
    let s = this.stores.get(name);
    if (!s) {
      s = new Map();
      this.stores.set(name, s);
    }
    return s;
  }

  async put(store: string, value: StoredLevel): Promise<void> {
    this.store(store).set(value.id, structuredClone(value));
  }

  async get(store: string, id: string): Promise<StoredLevel | undefined> {
    const v = this.store(store).get(id);
    return v ? structuredClone(v) : undefined;
  }

  async delete(store: string, id: string): Promise<void> {
    this.store(store).delete(id);
  }

  async list(store: string): Promise<StoredLevel[]> {
    return [...this.store(store).values()].map((v) => structuredClone(v));
  }
}

function makeBackend(): Backend {
  if (typeof indexedDB !== 'undefined') return new IdbBackend();
  console.warn('IndexedDB unavailable; editor persistence is in-memory only for this session.');
  return new MemoryBackend();
}

export class LevelStorage {
  private backend: Backend;

  constructor(backend?: Backend) {
    this.backend = backend ?? makeBackend();
  }

  async saveLevel(id: string, level: LevelData): Promise<void> {
    await this.backend.put(LEVELS, {
      id,
      name: level.meta.name,
      updatedAt: new Date().toISOString(),
      text: stringifyLevel(level),
    });
  }

  /** Returns validated level data; throws LevelValidationError on corrupt rows. */
  async loadLevel(id: string): Promise<LevelData | null> {
    const row = await this.backend.get(LEVELS, id);
    return row ? parseLevel(row.text) : null;
  }

  async deleteLevel(id: string): Promise<void> {
    await this.backend.delete(LEVELS, id);
    await this.backend.delete(DRAFTS, id);
  }

  async listLevels(): Promise<Array<{ id: string; name: string; updatedAt: string }>> {
    const rows = await this.backend.list(LEVELS);
    return rows
      .map(({ id, name, updatedAt }) => ({ id, name, updatedAt }))
      .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
  }

  // --- Crash-safe drafts -----------------------------------------------------

  async saveDraft(id: string, level: LevelData): Promise<void> {
    await this.backend.put(DRAFTS, {
      id,
      name: level.meta.name,
      updatedAt: new Date().toISOString(),
      text: stringifyLevel(level),
    });
  }

  async loadDraft(id: string): Promise<{ level: LevelData; updatedAt: string } | null> {
    const row = await this.backend.get(DRAFTS, id);
    if (!row) return null;
    return { level: parseLevel(row.text), updatedAt: row.updatedAt };
  }

  async clearDraft(id: string): Promise<void> {
    await this.backend.delete(DRAFTS, id);
  }

  // --- Version snapshots (stored alongside drafts, prefixed keys) -------------

  async saveSnapshot(levelId: string, level: LevelData): Promise<void> {
    await this.backend.put(DRAFTS, {
      id: `snap:${levelId}:${Date.now()}`,
      name: level.meta.name,
      updatedAt: new Date().toISOString(),
      text: stringifyLevel(level),
    });
  }

  async listSnapshots(levelId: string): Promise<Array<{ id: string; updatedAt: string }>> {
    const rows = await this.backend.list(DRAFTS);
    return rows
      .filter((r) => r.id.startsWith(`snap:${levelId}:`))
      .map(({ id, updatedAt }) => ({ id, updatedAt }))
      .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
  }

  async loadSnapshot(id: string): Promise<LevelData | null> {
    const row = await this.backend.get(DRAFTS, id);
    return row ? parseLevel(row.text) : null;
  }
}
